import React, { useEffect, useState, useContext } from 'react';
import api, { resolveScopedRestaurantId } from '../../utils/api';
import { AuthContext } from '../../context/AuthContext';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

export default function AnalyticsDashboard() {
  const { user } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [forecasts, setForecasts] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForecasts, setShowForecasts] = useState(true);
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState('');

  // Load restaurants on mount
  useEffect(() => {
    const loadRestaurants = async () => {
      try {
        const response = await api.get('/restaurants');
        setRestaurants(response.data.data || []);
      } catch (error) {
        console.error('Error fetching restaurants:', error);
      }
    };
    loadRestaurants();
  }, []);

  // Set default restaurant based on user role
  useEffect(() => {
    if (user) {
      if (user.role === 'staff' && user.restaurant_id) {
        setSelectedRestaurant(user.restaurant_id.toString());
        localStorage.setItem('selected_restaurant_id', user.restaurant_id.toString());
      } else if (user.role === 'admin') {
        const savedRestaurantId = localStorage.getItem('selected_restaurant_id');
        const initial = savedRestaurantId || resolveScopedRestaurantId(user)?.toString() || '';
        setSelectedRestaurant(initial);
        if (initial) {
          localStorage.setItem('selected_restaurant_id', initial);
        }
      }
    }
  }, [user]);

  useEffect(() => {
    if (selectedRestaurant && selectedRestaurant.trim()) {
      localStorage.setItem('selected_restaurant_id', selectedRestaurant.trim());
      return;
    }

    if (user?.role === 'admin') {
      localStorage.removeItem('selected_restaurant_id');
    }
  }, [selectedRestaurant, user]);

  // Fetch analytics data when restaurant changes
  useEffect(() => {
    if (user) {
      fetchAnalyticsData();
    }
  }, [selectedRestaurant, user]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      
      // Build params for restaurant filtering
      const params = {};
      if (selectedRestaurant && selectedRestaurant.trim()) {
        params.restaurant_id = parseInt(selectedRestaurant);
      } else if (user?.role === 'staff' && user?.restaurant_id) {
        params.restaurant_id = user.restaurant_id;
      }
      
      // Fetch traditional analytics
      const dashboardResponse = await api.get('/analytics/dashboard', { params });
      if (dashboardResponse.data.success) {
        setData(dashboardResponse.data.data);
      }
      
      // Fetch forecasts
      try {
        const forecastsResponse = await api.get('/analytics/forecasts', { params });
        if (forecastsResponse.data.success) {
          setForecasts(forecastsResponse.data.data);
        }
      } catch (err) {
        console.warn('Forecasts not available:', err.message);
      }
      
      // Fetch KPIs
      try {
        const kpisResponse = await api.get('/analytics/kpis', { params });
        if (kpisResponse.data.success) {
          setKpis(kpisResponse.data.data);
        }
      } catch (err) {
        console.warn('KPIs not available:', err.message);
      }
      
      // Fetch 90-day trends
      try {
        const trendsResponse = await api.get('/analytics/trends/90days', { params });
        if (trendsResponse.data.success) {
          setTrends(trendsResponse.data.data);
        }
      } catch (err) {
        console.warn('Trends not available:', err.message);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 m-6">
        <p className="text-red-600 font-semibold">{error}</p>
        <button
          onClick={fetchAnalyticsData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data || !data.summary) return null;

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

  // Helper function to safely format numbers
  const formatNumber = (value) => {
    const num = parseFloat(value) || 0;
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4 sm:mb-8 bg-white rounded-lg shadow-md p-4 sm:p-6">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">Complete business insights and performance metrics</p>
        </div>

        {/* Restaurant Selector - Show for Admins only */}
        {user?.role === 'admin' && restaurants.length > 0 && (
          <div className="mb-4 sm:mb-8 bg-white rounded-lg shadow-md p-4 sm:p-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Select Restaurant
            </label>
            <select
              value={selectedRestaurant}
              onChange={(e) => setSelectedRestaurant(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Choose a restaurant</option>
              {restaurants.map((restaurant) => (
                <option key={restaurant.restaurant_id} value={restaurant.restaurant_id.toString()}>
                  {restaurant.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4 lg:gap-6 mb-4 sm:mb-8">
          <SummaryCard
            title="Total Revenue"
            value={`₱${formatNumber(data.summary.total_revenue)}`}
            subtext="From all sources"
            color="bg-blue-50 border-blue-200"
          />
          <SummaryCard
            title="Transaction Revenue"
            value={`₱${formatNumber(data.summary.transaction_revenue)}`}
            subtext={`${data.summary.total_transactions || 0} transactions`}
            color="bg-green-50 border-green-200"
          />
          <SummaryCard
            title="Event Revenue"
            value={`₱${formatNumber(data.summary.event_revenue)}`}
            subtext={`${data.summary.total_events || 0} events`}
            color="bg-purple-50 border-purple-200"
          />
          <SummaryCard
            title="Avg Transaction Value"
            value={`₱${formatNumber(data.summary.average_transaction)}`}
            subtext="Per order"
            color="bg-orange-50 border-orange-200"
          />
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6 mb-3 sm:mb-6">
          {/* Daily Revenue Trend */}
          <ChartCard title="Daily Revenue Trend (Transactions Only)" description="Transaction revenue from menu orders over the past 30 days">
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={data.daily_revenue || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="formatted_date" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip formatter={(value) => `₱${formatNumber(value)}`} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#3B82F6"
                  dot={false}
                  name="Transaction Revenue"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Monthly Revenue Trend */}
          <ChartCard title="Monthly Revenue Trend (Transactions Only)" description="Revenue from menu orders over the past 12 months">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.monthly_revenue || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="formatted_date" />
                <YAxis />
                <Tooltip formatter={(value) => `₱${formatNumber(value)}`} />
                <Legend />
                <Bar dataKey="revenue" fill="#10B981" name="Transaction Revenue" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Payment Method Distribution */}
          <ChartCard title="Payment Method Distribution" description="Revenue breakdown by payment method">
            <div className="flex flex-col md:flex-row gap-4 md:gap-8">
              <ResponsiveContainer width="100%" height={250} className="md:w-1/2">
                <PieChart>
                  <Pie
                    data={data.payment_methods || []}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, count }) => `${name}: ${count || 0}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {(data.payment_methods || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `₱${formatNumber(value)}`} />
                </PieChart>
              </ResponsiveContainer>
              <div className="w-full md:w-1/2 overflow-y-auto max-h-64 md:max-h-80">
                {(data.payment_methods || []).map((method, idx) => (
                  <div key={idx} className="mb-3 pb-3 border-b last:border-b-0">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                      ></div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{method.name}</p>
                        <p className="text-sm text-gray-600">
                          ₱{formatNumber(method.value)}
                        </p>
                        <p className="text-xs text-gray-500">{method.count || 0} {(method.count || 0) === 1 ? 'transaction' : 'transactions'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </ChartCard>

          {/* Daily Order Volume */}
          <ChartCard title="Daily Order Volume" description="Number of orders per day">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.daily_orders || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="formatted_date" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="orders" fill="#F59E0B" name="Orders" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* Events by Type */}
        <ChartCard
          title="Event Bookings by Type"
          description="Distribution of event bookings and their revenue contribution"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
            <div>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={data.events_by_type || []}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, count }) => `${name}: ${count || 0}`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="revenue"
                  >
                    {(data.events_by_type || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `₱${formatNumber(value)}`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2 sm:space-y-3 overflow-y-auto max-h-80 sm:max-h-96">
              {(data.events_by_type || []).map((event, idx) => (
                <div key={idx} className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                      ></div>
                      <p className="font-semibold text-gray-900">{event.name}</p>
                    </div>
                    <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">
                      {event.count || 0} {(event.count || 0) === 1 ? 'booking' : 'bookings'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Revenue: <span className="font-semibold">₱{formatNumber(event.revenue)}</span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        </ChartCard>

        {/* AI-Powered Forecasts Section */}
        {forecasts && showForecasts && (
          <div className="mt-4 sm:mt-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 sm:mb-6">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900"> Predictive Analytics</h2>
                <p className="text-xs sm:text-base text-gray-600 mt-1">Advanced forecasting using MCDA (Orders), Linear Regression (Events), and Top Items Predictions</p>
              </div>
              <button
                onClick={() => setShowForecasts(!showForecasts)}
                className="px-3 py-1.5 sm:px-4 sm:py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition whitespace-nowrap"
              >
                Hide Forecasts
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6">
              {/* Daily Revenue Forecast - MCDA */}
              {forecasts.orders && (
                <ChartCard title="Revenue Forecast (30 Days)" description="MCDA-based predictions with 95% confidence interval">
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={forecasts.orders.filter(f => f.metric_name === 'daily_revenue').slice(0, 30)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="forecast_date" 
                        tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        angle={-45} 
                        textAnchor="end" 
                        height={80}
                      />
                      <YAxis tickFormatter={(value) => `₱${(value / 1000).toFixed(0)}k`} />
                      <Tooltip 
                        formatter={(value) => `₱${parseFloat(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                        labelFormatter={(date) => new Date(date).toLocaleDateString()}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="predicted_value" stroke="#3B82F6" strokeWidth={2} name="Predicted Revenue" dot={false} />
                      <Line type="monotone" dataKey="upper_bound" stroke="#93C5FD" strokeWidth={1} strokeDasharray="5 5" name="Upper Bound" dot={false} />
                      <Line type="monotone" dataKey="lower_bound" stroke="#93C5FD" strokeWidth={1} strokeDasharray="5 5" name="Lower Bound" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>
              )}

              {/* Order Volume Forecast - MCDA */}
              {forecasts.orders && (
                <ChartCard title="Order Volume Forecast" description="MCDA-based order predictions for next 30 days">
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={forecasts.orders.filter(f => f.metric_name === 'order_count').slice(0, 30)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="forecast_date" 
                        tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        angle={-45} 
                        textAnchor="end" 
                        height={80}
                      />
                      <YAxis />
                      <Tooltip 
                        formatter={(value) => Math.round(value)}
                        labelFormatter={(date) => new Date(date).toLocaleDateString()}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="predicted_value" stroke="#10B981" strokeWidth={2} name="Predicted Orders" dot={false} />
                      <Line type="monotone" dataKey="upper_bound" stroke="#6EE7B7" strokeWidth={1} strokeDasharray="5 5" name="Upper Bound" dot={false} />
                      <Line type="monotone" dataKey="lower_bound" stroke="#6EE7B7" strokeWidth={1} strokeDasharray="5 5" name="Lower Bound" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>
              )}

              {/* Event Demand Forecast - Linear Regression */}
              {forecasts.events && (
                <ChartCard title="Event Demand Forecast" description="Linear regression predictions for daily event bookings">
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={forecasts.events.filter(f => f.metric_name === 'event_bookings').slice(0, 30)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="forecast_date" 
                        tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        angle={-45} 
                        textAnchor="end" 
                        height={100}
                      />
                      <YAxis />
                      <Tooltip 
                        formatter={(value) => `${Math.round(value)} bookings`}
                        labelFormatter={(date) => new Date(date).toLocaleDateString()}
                      />
                      <Legend />
                      <Bar dataKey="predicted_value" fill="#8B5CF6" name="Predicted Bookings" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              )}

              {/* KPIs Summary */}
              {kpis && Object.keys(kpis).length > 0 && (
                <ChartCard title="Key Performance Indicators" description="Current metrics with trends">
                  <div className="space-y-3 sm:space-y-4 max-h-80 overflow-y-auto">
                    {Object.entries(kpis).map(([category, categoryKpis]) => (
                      <div key={category} className="border-b pb-4 last:border-b-0">
                        <h4 className="font-semibold text-gray-700 mb-2 capitalize">{category}</h4>
                        {categoryKpis.map((kpi, idx) => (
                          <div key={idx} className="bg-gray-50 p-3 rounded-lg mb-2">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-gray-900">{kpi.kpi_name}</p>
                                <p className="text-2xl font-bold text-blue-600">{kpi.current_value}</p>
                              </div>
                              <div className="text-right">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                                  kpi.trend === 'up' ? 'bg-green-100 text-green-800' :
                                  kpi.trend === 'down' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {kpi.trend === 'up' ? '↑' : kpi.trend === 'down' ? '↓' : '→'} {kpi.change_percentage}%
                                </span>
                                <p className="text-xs text-gray-500 mt-1">vs previous {kpi.period}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </ChartCard>
              )}

              {/* Top Selling Items Prediction */}
              {data && data.top_items && data.top_items.length > 0 && (
                <ChartCard title="Top Selling Items Prediction" description="Most profitable products based on historical trends">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b-2 border-gray-200">
                          <th className="text-left py-3 px-2 sm:px-4 font-semibold text-gray-700">Rank</th>
                          <th className="text-left py-3 px-2 sm:px-4 font-semibold text-gray-700">Item Name</th>
                          <th className="text-right py-3 px-2 sm:px-4 font-semibold text-gray-700">Revenue</th>
                          <th className="text-right py-3 px-2 sm:px-4 font-semibold text-gray-700">Orders</th>
                          <th className="text-right py-3 px-2 sm:px-4 font-semibold text-gray-700">Qty Sold</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.top_items.slice(0, 10).map((item, idx) => (
                          <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50 transition">
                            <td className="py-3 px-2 sm:px-4 text-gray-900 font-semibold">#{idx + 1}</td>
                            <td className="py-3 px-2 sm:px-4 text-gray-900">{item.name}</td>
                            <td className="py-3 px-2 sm:px-4 text-right text-blue-600 font-semibold">₱{parseFloat(item.revenue).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                            <td className="py-3 px-2 sm:px-4 text-right text-gray-700">{item.orders}</td>
                            <td className="py-3 px-2 sm:px-4 text-right text-gray-700">{item.quantity}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </ChartCard>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Summary Card Component
 */
function SummaryCard({ title, value, subtext, icon, color }) {
  return (
    <div className={`${color} border-2 rounded-lg p-3 sm:p-6 shadow-md hover:shadow-lg transition-all transform hover:scale-[1.02]`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-gray-600 text-[10px] sm:text-sm font-semibold truncate">{title}</p>
          <p className="text-sm sm:text-2xl font-bold text-gray-900 mt-1 sm:mt-2 break-words">{value}</p>
          <p className="text-[10px] sm:text-xs text-gray-600 mt-1 sm:mt-2 truncate">{subtext}</p>
        </div>
        <span className="text-xl sm:text-3xl flex-shrink-0">{icon}</span>
      </div>
    </div>
  );
}

/**
 * Chart Card Component
 */
function ChartCard({ title, description, children }) {
  return (
    <div className="bg-white rounded-lg shadow-lg p-3 sm:p-6 hover:shadow-xl transition-all border border-gray-200">
      <h2 className="text-sm sm:text-xl font-bold text-gray-900 mb-1">{title}</h2>
      <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-6">{description}</p>
      {children}
    </div>
  );
}
